"use client";

/**
 * App Payment Hook - 应用内支付相关功能
 * 封装 iOS 和 Android 端的应用内购买功能；支付模块独立，任意 UI 均可触发购买
 * 产品列表由前端 GET /products 获取后传入；购买成功后由 Web 调 confirm，原生不请求后端
 */

import { useCallback, useContext, useEffect, useState } from "react";

import { deviceDetector } from "@ospweb/sdk-ui";
import log from "logbeacon";
import type {
  AndroidBillingProductType,
  BridgeErrorCode,
  PaymentFetchProductsData,
  PaymentFetchProductItem,
  PaymentGetStatusData,
  PaymentRestoreData,
  PaymentPurchaseSuccessData,
} from "./types";
import { MessageType } from "./types";
import type { BridgeRequestError } from "./bridge";
import { getNativeErrorMessage, parseBridgeErrorPayload } from "./bridge";
import { useBridge } from "./useBridge";
import type { OfferDetail } from "@/src/features/api/memberApi";
import { PayChannel } from "@/src/features/api/memberApi";
import { OspApiClientContext } from "@/src/features";
import { bridgeOperationTracker } from "./bridgeOperationTracker";
import { confirmPurchaseBestEffort } from "./paymentConfirmHelper";
import {
  emitPaymentOutcome,
  subscribePaymentOutcomes,
} from "./paymentOutcomeBus";
import { schedulePaymentBridgeReconcile } from "./paymentBridgeReconcile";

function createPaymentOperationId(): string {
  return `pay_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @description 等待 promise 最多 timeoutMs；超时则继续执行（不抛错、不取消 promise）。
 * 用于避免“购买已完成，但后续网络请求（如 confirm）卡住”导致 UI 一直 loading。
 */
async function waitAtMost<T>(
  promise: Promise<T>,
  timeoutMs: number,
): Promise<{
  timedOut: boolean;
  value?: T;
}> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const value = await Promise.race([
      promise.then((v) => ({ timedOut: false as const, value: v })),
      new Promise<{ timedOut: true }>((resolve) => {
        timer = setTimeout(() => resolve({ timedOut: true }), timeoutMs);
      }),
    ]);
    return value;
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
}

/**
 * 产品信息（与原生返回格式一致，id 为商店侧 product_id）
 */
interface Product {
  id: string;
  displayName: string;
  description: string;
  displayPrice: string;
  price: number;
  currencyCode: string;
}

/**
 * 购买结果（含 confirm 所需字段：transactionId / purchaseToken）
 */
interface PurchaseResult {
  success: boolean;
  productId?: string;
  transactionId?: string;
  purchaseToken?: string;
  obfuscatedAccountId?: string;
  error?: string;
  /** 与 Bridge error.code 一致，便于按码展示本地化文案 */
  errorCode?: BridgeErrorCode;
}

/**
 * 购买选项（与后端 payment/check 返回一致，原样传给原生）
 */
interface PurchaseOptions {
  obfuscatedAccountId?: string;
  /** 后端产品 ID（MemberProduct.id），用于购买成功后调 payment/confirm 的 product_id */
  backendProductId?: string;
  /** iOS：check 返回的 offer_details（含 code、sign：key_id/nonce/signature/timestamp） */
  offerDetails?: OfferDetail[];
  /** iOS：check 返回的 owner_external_ref，作 appAccountToken */
  ownerExternalRef?: string;
  /** Android：check 返回的优惠对应 offerToken；有则优先用 */
  offerToken?: string;
  /** Android：商店侧 base plan id（来自产品 payment_config.plan_id），无 offerToken 时按此匹配方案下单 */
  planId?: string;
  /** Android：payment/check 的 old_purchase_token，用于 SubscriptionUpdateParams */
  oldPurchaseToken?: string;
}

/**
 * 订阅状态
 */
interface SubscriptionStatus {
  hasActiveSubscription: boolean;
  products: string[];
}

interface FetchProductsOptions {
  /** 给本次请求里的全部 productIds 使用同一类型 */
  productType?: AndroidBillingProductType;
  /** 按商品逐个指定类型（优先于 productType） */
  products?: PaymentFetchProductItem[];
}

interface QueryPurchasesOptions {
  productType?: AndroidBillingProductType;
}

/**
 * App Payment Hook 返回值
 */
export interface UseAppPaymentReturn {
  /** 产品列表 */
  products: Product[];
  /** 是否正在加载产品 */
  loading: boolean;
  /** 是否正在购买 */
  purchasing: boolean;
  /** 支付弹层关闭后正在向原生/服务端确认结果 */
  reconciling: boolean;
  /** 是否正在恢复购买 */
  restoring: boolean;
  /** 订阅状态 */
  subscriptionStatus: SubscriptionStatus | null;
  /** 错误信息 */
  error: string | null;
  /** 与 Bridge error.code 一致（失败时） */
  errorCode: BridgeErrorCode | null;
  /** 获取产品列表（productIds 由前端从 GET /products 的 payment_configs 取得，必传） */
  fetchProducts: (
    productIds: string[],
    options?: FetchProductsOptions,
  ) => Promise<Product[]>;
  /** 购买产品（可选传入 check 返回的优惠与用户绑定信息） */
  purchase: (
    productId: string,
    options?: PurchaseOptions,
  ) => Promise<PurchaseResult>;
  /** 恢复购买 */
  restore: (options?: QueryPurchasesOptions) => Promise<string[]>;
  /** 获取订阅状态 */
  getStatus: (options?: QueryPurchasesOptions) => Promise<SubscriptionStatus>;
  /** 打开商店/APP 订阅管理页（仅 App 内有效，无回调） */
  openSubscriptionManagement: () => void;
  /** 监听购买成功 */
  onPurchaseSuccess: (callback: (result: PurchaseResult) => void) => () => void;
  /** 监听购买失败/取消 */
  onPurchaseResult: (callback: (result: PurchaseResult) => void) => () => void;
  /** 监听产品列表更新 */
  onProductsUpdate: (callback: (products: Product[]) => void) => () => void;
  /** 监听恢复购买完成 */
  onRestoreComplete: (callback: (productIds: string[]) => void) => () => void;
  /** 监听订阅状态更新 */
  onStatusUpdate: (
    callback: (status: SubscriptionStatus) => void,
  ) => () => void;
  /** 清除错误 */
  clearError: () => void;
}

/**
 * 使用 App Payment 的 React Hook
 */
export function useAppPayment(): UseAppPaymentReturn {
  const { request, send, on, platform } = useBridge();
  const { memberApi } = useContext(OspApiClientContext);

  const isInApp = deviceDetector.isApp();
  const payChannel =
    platform === "ios" ? PayChannel.APPLE_IAP : PayChannel.GOOGLE_IAP;
  // 支付相关桥接 request 只需要快速 ACK；超时通常代表桥接不可用/未实现
  const paymentBridgeTimeoutMs = 60000;

  // confirm 是“最佳努力”：正常情况需要尽快落库同步权益，但不能让 UI 因网络卡住而无限 loading
  const confirmMaxWaitMs = 2500;

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<BridgeErrorCode | null>(null);

  /**
   * 获取产品列表（支持 Web 透传 Android productType，兼容旧参数）
   */
  const fetchProducts = useCallback(
    async (
      productIds: string[],
      options?: FetchProductsOptions,
    ): Promise<Product[]> => {
      if (!isInApp) {
        throw new Error("Payment is only available in App environment");
      }
      if (!productIds?.length) {
        throw new Error("productIds is required (from backend GET /products)");
      }

      setLoading(true);
      setError(null);
      setErrorCode(null);

      try {
        const payload: PaymentFetchProductsData = {
          productIds,
          productType: options?.productType,
          products: options?.products,
        };
        const response = await request(
          MessageType.PAYMENT_FETCH_PRODUCTS,
          payload,
          {
            timeoutMs: paymentBridgeTimeoutMs,
          },
        );

        if (response.success && response.data?.products) {
          const productList = response.data.products as Product[];
          setProducts(productList);
          return productList;
        } else {
          const bre = parseBridgeErrorPayload(response.error);
          const errorMessage =
            getNativeErrorMessage(response.error) ??
            getNativeErrorMessage(response.data) ??
            "Failed to fetch products";
          const e = new Error(errorMessage) as BridgeRequestError;
          if (bre) {
            e.bridgeError = bre;
          }
          throw e;
        }
      } catch (err) {
        const errorMessage =
          getNativeErrorMessage(err) ?? "Failed to fetch products";
        setError(errorMessage);
        setErrorCode((err as BridgeRequestError).bridgeError?.code ?? null);
        if (err instanceof Error) {
          throw err;
        }
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [isInApp, request],
  );

  /**
   * 购买产品（可选传入 check 返回的 offerDetails / ownerExternalRef / offerToken）
   */
  const purchase = useCallback(
    async (
      productId: string,
      options?: PurchaseOptions,
    ): Promise<PurchaseResult> => {
      if (!isInApp) {
        throw new Error("Payment is only available in App environment");
      }
      log.debug("[useAppPayment] purchase", { productId, options });
      setPurchasing(true);
      setReconciling(false);
      setError(null);
      setErrorCode(null);

      const operationId = createPaymentOperationId();
      bridgeOperationTracker.registerPaymentSession({
        operationId,
        storeProductId: productId,
        backendProductId: options?.backendProductId,
        payChannel,
        productType: "SUBS",
      });

      try {
        return new Promise<PurchaseResult>((resolve, reject) => {
          let resolved = false;
          let timeoutId: ReturnType<typeof setTimeout> | undefined;
          let unsubscribeBus: () => void = () => {};
          let unsubscribeSuccess: () => void = () => {};
          let unsubscribeResult: () => void = () => {};

          const cleanupListeners = () => {
            if (timeoutId) {
              clearTimeout(timeoutId);
            }
            unsubscribeBus();
            unsubscribeSuccess();
            unsubscribeResult();
          };

          const settleSuccess = async (
            data: PaymentPurchaseSuccessData & { verified?: boolean },
            sourceOperationId?: string,
          ) => {
            if (resolved) {
              return;
            }
            if (
              sourceOperationId &&
              sourceOperationId !== operationId &&
              data.productId &&
              data.productId !== productId
            ) {
              return;
            }
            resolved = true;
            setPurchasing(false);
            setReconciling(false);
            bridgeOperationTracker.clearPaymentSession(operationId);

            const result: PurchaseResult = {
              success: true,
              productId: data.productId ?? productId,
              transactionId: data.transactionId,
              purchaseToken: data.purchaseToken,
              obfuscatedAccountId: data.obfuscatedAccountId,
            };

            const purchaseToken =
              payChannel === PayChannel.APPLE_IAP
                ? data.transactionId
                : data.purchaseToken;
            if (purchaseToken) {
              const confirmWait = await waitAtMost(
                confirmPurchaseBestEffort({
                  payChannel,
                  purchaseToken,
                  backendProductId: options?.backendProductId,
                  memberApi,
                }),
                confirmMaxWaitMs,
              );
              if (confirmWait.timedOut) {
                log.warn(
                  "[useAppPayment] payment/confirm timeout; continue without blocking UI",
                  { confirmMaxWaitMs },
                );
              }
            }

            resolve(result);
            cleanupListeners();
          };

          const settleFailure = (data: any, sourceOperationId?: string) => {
            if (resolved) {
              return;
            }
            if (sourceOperationId && sourceOperationId !== operationId) {
              return;
            }
            if (!data || (data.success !== false && !data.error)) {
              return;
            }
            resolved = true;
            setPurchasing(false);
            setReconciling(false);
            bridgeOperationTracker.clearPaymentSession(operationId);

            const errorMessage =
              getNativeErrorMessage(data?.error) ??
              getNativeErrorMessage(data) ??
              "Purchase cancelled or failed";
            const bre = parseBridgeErrorPayload(data?.error);
            setError(errorMessage);
            setErrorCode(bre?.code ?? null);
            const e = new Error(errorMessage) as BridgeRequestError;
            if (bre) {
              e.bridgeError = bre;
            }
            reject(e);
            cleanupListeners();
          };

          unsubscribeBus = subscribePaymentOutcomes((event) => {
            if (event.operationId && event.operationId !== operationId) {
              return;
            }
            if (event.kind === "success") {
              void settleSuccess(event.data, event.operationId);
            } else if (event.kind === "result") {
              settleFailure(event.data, event.operationId);
            }
          });

          unsubscribeSuccess = on(
            MessageType.PAYMENT_PURCHASE_SUCCESS,
            (data: PaymentPurchaseSuccessData & { verified?: boolean }) => {
              emitPaymentOutcome({
                kind: "success",
                data,
                operationId,
                source: "bridge-push",
              });
            },
          );

          unsubscribeResult = on(
            MessageType.PAYMENT_PURCHASE_RESULT,
            (data: any) => {
              emitPaymentOutcome({
                kind: "result",
                data,
                operationId,
                source: "bridge-push",
              });
            },
          );

          timeoutId = setTimeout(() => {
            if (resolved) {
              return;
            }
            setPurchasing(false);
            setReconciling(true);
            schedulePaymentBridgeReconcile("purchase-timeout");
          }, 300000);

          const payload: Record<string, unknown> = { productId };
          if (options?.obfuscatedAccountId != null) {
            payload.obfuscatedAccountId = options.obfuscatedAccountId;
          }
          if (options?.offerDetails != null) {
            payload.offerDetails = options.offerDetails;
          }
          if (options?.ownerExternalRef != null) {
            payload.ownerExternalRef = options.ownerExternalRef;
          }
          if (options?.offerToken != null) {
            payload.offerToken = options.offerToken;
          }
          if (options?.planId != null) {
            payload.planId = options.planId;
          }
          if (options?.oldPurchaseToken != null) {
            payload.oldPurchaseToken = options.oldPurchaseToken;
          }
          log.error(
            "[useAppPayment] 即将发往原生 PAYMENT_PURCHASE payload keys:",
            Object.keys(payload),
            "offerDetails:",
            payload.offerDetails != null ? "有" : "无",
            "ownerExternalRef:",
            payload.ownerExternalRef != null ? "有" : "无",
            "oldPurchaseToken:",
            payload.oldPurchaseToken != null ? "有" : "无",
          );

          request(MessageType.PAYMENT_PURCHASE, payload, {
            timeoutMs: paymentBridgeTimeoutMs,
            id: operationId,
          })
            .then(() => {
              // 购买流程已启动；结果经 push 或 resume reconcile 返回
            })
            .catch((err) => {
              log.error("[useAppPayment] payment/purchase failed", { err });
              if (!resolved) {
                resolved = true;
                setPurchasing(false);
                setReconciling(false);
                bridgeOperationTracker.clearPaymentSession(operationId);
                const errorMessage =
                  getNativeErrorMessage(err) ?? "Purchase failed";
                setError(errorMessage);
                setErrorCode(
                  (err as BridgeRequestError).bridgeError?.code ?? null,
                );
                const e = err instanceof Error ? err : new Error(errorMessage);
                cleanupListeners();
                reject(e);
              }
            });
        });
      } catch (err) {
        log.error("[useAppPayment] purchase failed", { err });
        setPurchasing(false);
        const errorMessage = getNativeErrorMessage(err) ?? "Purchase failed";
        setError(errorMessage);
        setErrorCode((err as BridgeRequestError).bridgeError?.code ?? null);
        if (err instanceof Error) {
          throw err;
        }
        throw new Error(errorMessage);
      }
    },
    [isInApp, request, on, payChannel, memberApi],
  );

  /**
   * 恢复购买
   */
  const restore = useCallback(
    async (options?: QueryPurchasesOptions): Promise<string[]> => {
      if (!isInApp) {
        throw new Error("Payment is only available in App environment");
      }

      setRestoring(true);
      setError(null);
      setErrorCode(null);

      try {
        const payload: PaymentRestoreData = {
          productType: options?.productType,
        };
        const response = await request(MessageType.PAYMENT_RESTORE, payload, {
          timeoutMs: paymentBridgeTimeoutMs,
        });

        if (response.success && response.data?.products) {
          const productIds = response.data.products as string[];
          setRestoring(false);
          return productIds;
        } else {
          const bre = parseBridgeErrorPayload(response.error);
          const errorMessage =
            getNativeErrorMessage(response.error) ??
            getNativeErrorMessage(response.data) ??
            "Failed to restore purchases";
          const e = new Error(errorMessage) as BridgeRequestError;
          if (bre) {
            e.bridgeError = bre;
          }
          throw e;
        }
      } catch (err) {
        setRestoring(false);
        const errorMessage =
          getNativeErrorMessage(err) ?? "Failed to restore purchases";
        setError(errorMessage);
        setErrorCode((err as BridgeRequestError).bridgeError?.code ?? null);
        if (err instanceof Error) {
          throw err;
        }
        throw new Error(errorMessage);
      }
    },
    [isInApp, request],
  );

  /**
   * 获取订阅状态
   */
  const getStatus = useCallback(
    async (options?: QueryPurchasesOptions): Promise<SubscriptionStatus> => {
      if (!isInApp) {
        throw new Error("Payment is only available in App environment");
      }

      setError(null);
      setErrorCode(null);

      try {
        const payload: PaymentGetStatusData = {
          productType: options?.productType,
        };
        const response = await request(
          MessageType.PAYMENT_GET_STATUS,
          payload,
          {
            timeoutMs: paymentBridgeTimeoutMs,
          },
        );

        if (response.success && response.data) {
          const status: SubscriptionStatus = {
            hasActiveSubscription: response.data.hasActiveSubscription ?? false,
            products: response.data.products ?? [],
          };
          setSubscriptionStatus(status);
          return status;
        } else {
          const bre = parseBridgeErrorPayload(response.error);
          const errorMessage =
            getNativeErrorMessage(response.error) ??
            getNativeErrorMessage(response.data) ??
            "Failed to get subscription status";
          const e = new Error(errorMessage) as BridgeRequestError;
          if (bre) {
            e.bridgeError = bre;
          }
          throw e;
        }
      } catch (err) {
        const errorMessage =
          getNativeErrorMessage(err) ?? "Failed to get subscription status";
        setError(errorMessage);
        setErrorCode((err as BridgeRequestError).bridgeError?.code ?? null);
        if (err instanceof Error) {
          throw err;
        }
        throw new Error(errorMessage);
      }
    },
    [isInApp, request],
  );

  /**
   * 监听购买成功
   */
  const onPurchaseSuccess = useCallback(
    (callback: (result: PurchaseResult) => void) => {
      return on(MessageType.PAYMENT_PURCHASE_SUCCESS, (data: any) => {
        callback({
          success: true,
          productId: data.productId,
          transactionId: data.transactionId,
          purchaseToken: data.purchaseToken,
          obfuscatedAccountId: data.obfuscatedAccountId,
        });
      });
    },
    [on],
  );

  /**
   * 监听购买失败/取消
   */
  const onPurchaseResult = useCallback(
    (callback: (result: PurchaseResult) => void) => {
      return on(MessageType.PAYMENT_PURCHASE_RESULT, (data: any) => {
        if (data.success === false) {
          const be = parseBridgeErrorPayload(data.error);
          const result: PurchaseResult = {
            success: false,
            error:
              be?.errMsg ??
              getNativeErrorMessage(data.error) ??
              (typeof data.error === "string" ? data.error : undefined) ??
              "Purchase failed",
            errorCode: be?.code,
          };
          callback(result);
        }
      });
    },
    [on],
  );

  /**
   * 监听产品列表更新
   */
  const onProductsUpdate = useCallback(
    (callback: (products: Product[]) => void) => {
      return on(MessageType.PAYMENT_PRODUCTS, (data: any) => {
        if (data.products) {
          const productList = data.products as Product[];
          setProducts(productList);
          callback(productList);
        }
      });
    },
    [on],
  );

  /**
   * 监听恢复购买完成
   */
  const onRestoreComplete = useCallback(
    (callback: (productIds: string[]) => void) => {
      return on(MessageType.PAYMENT_RESTORE_COMPLETE, (data: any) => {
        if (data.products) {
          const productIds = data.products as string[];
          callback(productIds);
        }
      });
    },
    [on],
  );

  /**
   * 监听订阅状态更新
   */
  const onStatusUpdate = useCallback(
    (callback: (status: SubscriptionStatus) => void) => {
      return on(MessageType.PAYMENT_STATUS, (data: any) => {
        const status: SubscriptionStatus = {
          hasActiveSubscription: data.hasActiveSubscription ?? false,
          products: data.products ?? [],
        };
        setSubscriptionStatus(status);
        callback(status);
      });
    },
    [on],
  );

  /**
   * 清除错误
   */
  const clearError = useCallback(() => {
    setError(null);
    setErrorCode(null);
  }, []);

  /**
   * 打开商店/APP 订阅管理页（仅 App 内有效，无回调）
   * iOS：App Store 订阅管理；Android：Google Play 订阅管理
   */
  const openSubscriptionManagement = useCallback(() => {
    if (!isInApp) {
      return;
    }
    send(MessageType.PAYMENT_OPEN_MANAGEMENT);
  }, [isInApp, send]);

  // 初始化时监听无 id 请求的通知消息
  useEffect(() => {
    // 监听产品列表更新（无 id 请求时）
    const unsubscribeProducts = onProductsUpdate(() => {
      // 产品列表已通过 setProducts 更新
    });

    // 监听恢复购买完成（无 id 请求时）
    const unsubscribeRestore = onRestoreComplete(() => {
      // 恢复完成，可以更新 UI
    });

    // 监听订阅状态更新（无 id 请求时）
    const unsubscribeStatus = onStatusUpdate(() => {
      // 订阅状态已通过 setSubscriptionStatus 更新
    });

    return () => {
      unsubscribeProducts();
      unsubscribeRestore();
      unsubscribeStatus();
    };
  }, [onProductsUpdate, onRestoreComplete, onStatusUpdate]);

  return {
    products,
    loading,
    purchasing,
    reconciling,
    restoring,
    subscriptionStatus,
    error,
    errorCode,
    fetchProducts,
    purchase,
    restore,
    getStatus,
    openSubscriptionManagement,
    onPurchaseSuccess,
    onPurchaseResult,
    onProductsUpdate,
    onRestoreComplete,
    onStatusUpdate,
    clearError,
  };
}
